import { ApiService } from './api';

/**
 * Project Metrics interface
 */
export interface ProjectMetrics {
  totalProjects: number;
  activeProjects: number;
  completedProjects: number;
  onTimeDelivery: number;
  budgetUtilization: number;
  teamProductivity: number;
  clientSatisfaction: number;
  avgProjectDuration: number;
}

/**
 * Project Data interface
 */
export interface ProjectData {
  id: number;
  name: string;
  status: 'active' | 'completed' | 'delayed' | 'on-hold';
  progress: number;
  budget: number;
  spent: number;
  teamSize: number;
  daysRemaining: number;
  tasksCompleted: number;
  totalTasks: number;
  clientRating: number;
}

/**
 * Analytics Data interface
 */
export interface AnalyticsData {
  metrics: ProjectMetrics;
  projects: ProjectData[];
}

/**
 * Project Analytics Service
 * Handles all API calls for PM project analytics
 */
export class ProjectAnalyticsService {
  /**
   * Get project analytics data
   */
  static async getAnalytics(filters?: {
    timeframe?: 'week' | 'month' | 'quarter' | 'year';
    project_id?: string;
  }): Promise<AnalyticsData> {
    try {
      const response = await ApiService.get<{
        success: boolean;
        data: AnalyticsData;
      }>('/project-manager/analytics', { params: filters });
      
      return response.data;
    } catch (error) {
      console.error('Error fetching project analytics:', error);
      throw new Error('Failed to load project analytics');
    }
  }

  /**
   * Export analytics report
   */
  static async exportReport(filters?: {
    timeframe?: string;
    project_id?: string;
    format?: 'pdf' | 'csv' | 'xlsx';
  }): Promise<Blob> {
    try {
      const response = await ApiService.get<any>(
        '/project-manager/analytics/export',
        {
          params: filters,
          responseType: 'blob'
        }
      );
      
      return response.data;
    } catch (error) {
      console.error('Error exporting analytics report:', error);
      throw new Error('Failed to export report');
    }
  }

  /**
   * Get project-specific analytics
   */
  static async getProjectAnalytics(projectId: number, timeframe?: string): Promise<{
    progress: number;
    budget: {
      total: number;
      spent: number;
      remaining: number;
      utilizationRate: number;
    };
    tasks: {
      total: number;
      completed: number;
      inProgress: number;
      completionRate: number;
    };
    team: {
      size: number;
      productivity: number;
      utilization: number;
    };
    timeline: {
      startDate: string;
      endDate: string;
      daysRemaining: number;
      daysElapsed: number;
      isOnTrack: boolean;
    };
  }> {
    try {
      const response = await ApiService.get<{
        success: boolean;
        data: any;
      }>(`/project-manager/projects/${projectId}/analytics`, {
        params: { timeframe }
      });
      
      return response.data;
    } catch (error) {
      console.error('Error fetching project-specific analytics:', error);
      throw new Error('Failed to load project analytics');
    }
  }

  /**
   * Get comparative analytics (compare projects)
   */
  static async getComparativeAnalytics(projectIds: number[]): Promise<{
    projects: Array<{
      id: number;
      name: string;
      metrics: {
        progress: number;
        budgetUtilization: number;
        taskCompletion: number;
        teamProductivity: number;
      };
    }>;
  }> {
    try {
      const response = await ApiService.post<{
        success: boolean;
        data: any;
      }>('/project-manager/analytics/compare', {
        project_ids: projectIds
      });
      
      return response.data;
    } catch (error) {
      console.error('Error fetching comparative analytics:', error);
      throw new Error('Failed to load comparative analytics');
    }
  }

  /**
   * Get trend data for charts
   */
  static async getTrendData(metric: string, timeframe: string = 'month'): Promise<{
    labels: string[];
    values: number[];
  }> {
    try {
      const response = await ApiService.get<{
        success: boolean;
        data: {
          labels: string[];
          values: number[];
        };
      }>('/project-manager/analytics/trends', {
        params: { metric, timeframe }
      });
      
      return response.data;
    } catch (error) {
      console.error('Error fetching trend data:', error);
      throw new Error('Failed to load trend data');
    }
  }
}

export default ProjectAnalyticsService;
